import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import FlexComp from "../reusable/flex/Flex";
import { Span } from "../reusable/typography/Typography.style";
import { Line } from "../client/rentalProperty/RentalProperty.style";
import { apiReq } from "services/Requests";
import { API_ROUTES } from "utils/apiRoutes.constant";
import { useNavigate } from "react-router-dom";
import { SyncLoader } from "react-spinners";

const Notification = ({ isOpenModal, setIsOpenModal }) => {
  const limit = 10;
  const { user } = JSON.parse(localStorage.getItem("userData"));
  const navigate = useNavigate();

  const scrollContainerRef = useRef(null);
  const [page, setPage] = useState(1);
  const [notifications, setNotifications] = useState([]);
  // Mark as read loading state
  const [_markAllAsRead, _setMarkAllAsRead] = useState(false);
  const [_notifications, _setNotifications] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      _setMarkAllAsRead(true);
      const response = await apiReq(
        API_ROUTES.markAllNotificationsAsRead,
        "GET"
      );
      const updatedNotifications = notifications.map((n) => {
        return {
          ...n,
          receivers: n.receivers.map((r) => {
            if (r.id === user?._id) {
              return { ...r, isRead: true };
            }
            return r;
          }),
        };
      });
      setNotifications(updatedNotifications);

      console.log(response, "__response__");
    } catch (e) {
      console.log(e, "__error__");
    } finally {
      _setMarkAllAsRead(false);
    }
  }, [notifications, user]);

  const handleNotificationClick = async (notification) => {
    try {
      const link = notification?.receivers?.find(
        (r) => r.id === user?._id
      )?.link;

      if (link) {
        navigate(link);
      }
      const response = await apiReq(
        `${API_ROUTES.markNotificationAsRead}/${notification?._id}`,
        "GET"
      );

      if (response?.data?.data?.status === true) {
        const updatedNotifications = notifications.map((item) => {
          // Ensure item and item.receivers are defined before proceeding
          if (
            item &&
            item._id === notification?._id &&
            Array.isArray(item.receivers) &&
            item.receivers.length > 0
          ) {
            // Return a new object, don't mutate the original item
            return {
              ...item,
              receivers: item.receivers.map((rec) => {
                if (rec?.id === response?.data?.data?.id) {
                  return {
                    ...rec,
                    isRead: true,
                  };
                }
                return rec;
              }),
            };
          }
          // Return the item as is if it doesn't match or receivers is not an array
          return item;
        });

        setNotifications(updatedNotifications);
      }
      console.log(notification, "__notification__");

      // console.log(response, "__response__");
    } catch (e) {
      console.log(e, "__error__");
    } finally {
      setIsOpenModal(false);
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      if (!_notifications) {
        _setNotifications(true);
        const response = await apiReq(
          `${API_ROUTES.notifications}?page=${page}&limit=${limit}`,
          "GET"
        );
        setNotifications([...notifications, ...response?.data?.data]);
        setTotalItems(response?.data?.pagination?.total);
      }
    } catch (error) {
      console.log(error, "__error__");
    } finally {
      _setNotifications(false);
    }
  }, [page]);

  useEffect(() => {
    if (isOpenModal) {
      fetchNotifications();
    }
  }, [fetchNotifications, isOpenModal]);

  const handleScroll = useCallback(
    (e) => {
      const container = e.currentTarget;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        console.log("set page", notifications?.length, totalItems);
        if (notifications?.length < totalItems && !_notifications) {
          setPage(page + 1);
        }
      }
    },
    [page, totalItems, notifications, _notifications]
  );

  // console.log(notifications, "__notifications__");

  return (
    <>
      {/* Overlay */}
      {isOpenModal && <Overlay onClick={() => setIsOpenModal(false)} />}

      {/* Drawer */}
      <DrawerContainer isOpen={isOpenModal}>
        <DrawerContent>
          <DrawerHeader>
            <Span color="#000000" fontSize="16px" fontWeight="500">
              Notifications
            </Span>
            <CloseIcon
              sx={{ color: "#000", fontSize: "20px", cursor: "pointer" }}
              onClick={() => setIsOpenModal(false)}
            />
          </DrawerHeader>
          {notifications?.length > 0 && (
            <NotificationActions>
              <NotificationActionButton
                onClick={handleMarkAllAsRead}
                disabled={_markAllAsRead}
              >
                Mark All as Read
              </NotificationActionButton>
            </NotificationActions>
          )}
          <NotificationList ref={scrollContainerRef} onScroll={handleScroll}>
            {notifications?.map((item, index) => {
              const isRead = item.receivers.find(
                (r) => r.id === user?._id
              )?.isRead;
              return (
                <NotificationItem
                  key={index}
                  className={isRead ? "read" : ""}
                  onClick={() => handleNotificationClick(item)}
                >
                  <NotificationText>{item?.message}</NotificationText>
                  <NotificationTime>{item?.createdAt}</NotificationTime>
                </NotificationItem>
              );
            })}
            {!notifications?.length && (
              <NotificationEmpty>
                {/* <NotificationText>No notifications</NotificationText> */}
                <SyncLoader color={"#252424"} size={8} />
              </NotificationEmpty>
            )}
          </NotificationList>
        </DrawerContent>
      </DrawerContainer>
    </>
  );
};

export default Notification;

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 999;
`;

const DrawerContainer = styled.div`
  position: fixed;
  top: 0;
  right: ${({ isOpen }) => (isOpen ? "0" : "-350px")};
  width: 350px;
  height: 100vh;
  background: #ffffff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: right 0.3s ease-in-out;
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 20px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 20px;
`;

const NotificationActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 20px;
`;

const NotificationActionButton = styled.button`
  background: transparent;
  color: blue;
  cursor: pointer;
  border: none;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const NotificationEmpty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const NotificationList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const NotificationItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 15px;
  border-radius: 5px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  cursor: pointer;

  &:hover {
    background: #f8f9fa;
  }

  &.read {
    background: #f8f9fa;
  }
`;

const NotificationText = styled.div`
  font-size: 14px;
  color: #212529;
  line-height: 1.4;
`;

const NotificationTime = styled.div`
  font-size: 12px;
  color: #6c757d;
`;
